// Chrome Extensions for Students in 2026: A Study Stack You Will Actually Keep Installed
//
// SEO roundup post. Primary target: "Chrome extensions for students".
// Long-tail: "browser extensions for college students", "study tools Chrome",
// "tab manager for students", "focus extensions for students".
// Ported from marketing-blackmount/content/articles/best-chrome-extensions-students.md
// (2026-02-21), condensed and reframed by category. Blackmount facts: Pro
// $0.99/mo, free Guest Mode (offline, no account), Chrome/Firefox/Edge/Brave/
// Vivaldi/Opera + iPhone, voice notes, AI chat capture, cloud sync.
// Competitor facts re-verified 2026-09-30: OneTab HAS encrypted Backup & Sync as
// a PAID subscription (one-tab.com/sync) — the free extension stays local. It
// restores all tabs at once and has folders/reordering/search, so "local-only",
// "URL lists you re-click", and "per-browser lists" are all false. Its
// uninstall/profile-reset data-loss warning still applies to the local copy.

import React from 'react';
import {
  Typography,
  Box,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Link as MuiLink,
  Divider,
} from '@mui/material';
import { Link as RouterLink } from 'react-router-dom';

const AUTHORS = ['Mehrdad Shirangi'];
const PUBLISHED_AT = '2026-08-12';
const UPDATED_AT = '2026-08-12';

const meta = {
  slug: 'chrome-extensions-for-students',
  title: 'Chrome Extensions for Students in 2026: A Study Stack You Will Actually Keep Installed',
  description: 'A short, honest set of Chrome extensions for students in 2026 — session saving, note capture, focus, citations, writing, and AI — with pricing and the trade-offs nobody mentions.',
  publishedAt: PUBLISHED_AT,
  updatedAt: UPDATED_AT,
  authors: AUTHORS,
  tags: ['chrome extensions for students', 'study tools', 'student productivity', 'tab manager', 'citations'],
  readingTime: '8 min',
  ogImage: '/blackmount-logo.jpg',
};

function Section({ id, title, children }) {
  return (
    <Box component="section" id={id} sx={{ mt: 5 }}>
      <Typography variant="h4" component="h2" fontWeight={700} sx={{ mb: 2, scrollMarginTop: 96 }}>
        {title}
      </Typography>
      {children}
    </Box>
  );
}

function P({ children }) {
  return (
    <Typography variant="body1" sx={{ mb: 2, lineHeight: 1.75, fontSize: '1.05rem' }}>
      {children}
    </Typography>
  );
}

function Body() {
  return (
    <>
      <P>
        Most of your academic life happens inside a browser tab: readings, lecture recordings,
        Google Docs essays, library databases, group project threads, and — honestly — the
        procrastination too. The right handful of extensions turns that browser into a study
        tool. The wrong handful turns it into a slow machine with fourteen toolbar icons you
        never click.
      </P>
      <P>
        This is a short list, grouped by the problem it solves, with the trade-offs included.
        Install the two or three that match your actual pain point this semester and ignore
        the rest.
      </P>

      <Section id="sessions" title="Saving your work: session managers">
        <P>
          Closing a browser with thirty tabs open across three topics feels like erasing part
          of your brain, because it more or less is. Session managers exist for that moment.
        </P>
        <P>
          <MuiLink component={RouterLink} to="/">Blackmount</MuiLink> — full disclosure, this
          is our product — saves an entire session in one click: every open tab, plus notes and
          a voice recording if you want one. The voice note is what students keep coming back
          for. You have a lecture recording open next to two papers and a textbook chapter;
          before you close everything you hit record and say &quot;the second paper contradicts
          the lecture on sample size, check figure 3 against table 2.&quot; Ten seconds now saves
          twenty minutes of re-reading next week. AI chat threads you have captured from
          ChatGPT, Claude, Gemini and others are saved and searchable alongside the tabs, which
          matters when half your studying happens in a chat window. It works on Chrome, Firefox,
          Edge, Brave, Vivaldi, and Opera, plus iPhone. Guest Mode is free, works offline, and
          needs no account; Pro is $0.99/month and syncs sessions across devices, so the session
          you saved on a library computer is waiting on your laptop. The honest limit: if you
          keep five tabs open and mostly need a timer, this is more tool than you need.
        </P>
        <P>
          <MuiLink href="https://tab-session-manager.sienori.com" target="_blank" rel="noopener noreferrer">Tab Session Manager</MuiLink>{' '}
          is free and open source, saves and restores named sessions with a keyboard shortcut,
          auto-saves against crashes, and exports to JSON, Markdown, or CSV. Everything lives in
          local browser storage — no sync, no cross-device restore — so if the laptop dies, the
          sessions go with it unless you exported.{' '}
          <MuiLink href="https://www.one-tab.com" target="_blank" rel="noopener noreferrer">OneTab</MuiLink>{' '}
          collapses forty tabs out of the way and brings them all back at once, which is
          genuinely useful as an emergency reset, and it sorts saves into folders you can
          search. Encrypted sync and backup across your devices exists too, but it is a
          paid subscription — on the free extension everything stays in local browser
          storage, and OneTab's own guidance warns that copy does not survive an uninstall
          or a profile reset. On a student budget that is the thing to weigh. If your
          problem is specifically{' '}
          <MuiLink component={RouterLink} to="/blog/save-tabs-open-another-computer">
            picking tabs back up on another computer
          </MuiLink>, that comparison goes deeper.
        </P>
      </Section>

      <Section id="capture" title="Capturing what you read: clippers and quick notes">
        <P>
          <MuiLink href="https://www.notion.com/web-clipper" target="_blank" rel="noopener noreferrer">Notion Web Clipper</MuiLink>{' '}
          is the one to install if Notion already holds your course notes. Its real value is
          filing at the moment of capture: you set the course, priority, or assignment as
          database properties right in the popup instead of dumping another unsorted bookmark.
          It needs a Notion account, and adopting Notion purely for the clipper is not worth it.
        </P>
        <P>
          <MuiLink href="https://keep.google.com" target="_blank" rel="noopener noreferrer">Google Keep</MuiLink>{' '}
          is free and the fastest path from &quot;I should remember this&quot; to a saved note,
          syncing everywhere you are signed into Google. Built-in OCR pulls text out of images,
          which is handy for a photographed slide.{' '}
          <MuiLink href="https://evernote.com/features/webclipper" target="_blank" rel="noopener noreferrer">Evernote Web Clipper</MuiLink>{' '}
          still has the most flexible capture modes — full page, simplified article, bookmark,
          annotated screenshot — but Evernote's free tier has tightened enough that heavy
          clippers will hit limits.
        </P>
      </Section>

      <Section id="focus" title="Staying on task: focus tools">
        <P>
          <MuiLink href="https://www.forestapp.cc" target="_blank" rel="noopener noreferrer">Forest</MuiLink>{' '}
          (free) turns a focus session into a growing tree that dies if you open a blocked site.
          It sounds gimmicky and it works — the reluctance to kill a tree is a surprisingly
          effective deterrent. Data stays local.
        </P>
        <P>
          <MuiLink href="https://www.stayfocusd.com" target="_blank" rel="noopener noreferrer">StayFocusd</MuiLink>{' '}
          (free) is enforcement rather than encouragement: daily time allowances per site, and a
          Nuclear Option that blocks chosen sites for a set number of hours and cannot be
          canceled by restarting Chrome or clearing the cache. It can block specific paths or
          page elements, so you can keep YouTube lectures while killing Shorts, comments, and
          recommendations. If you want the timing without the blocking,{' '}
          <MuiLink href="https://chromewebstore.google.com/detail/marinara-pomodoro%C2%AE-assist/ldipcbpaocekfooobnbcddclnhejkcpn" target="_blank" rel="noopener noreferrer">Marinara Timer</MuiLink>{' '}
          is an open-source Pomodoro timer that does exactly that and nothing else.
        </P>
      </Section>

      <Section id="citations" title="Sources and citations">
        <P>
          <MuiLink href="https://www.zotero.org" target="_blank" rel="noopener noreferrer">Zotero Connector</MuiLink>{' '}
          is free and the strongest choice for anyone writing papers with real bibliographies.
          One click on a journal article, book record, or news story captures the full metadata
          and attaches the PDF when it exists; it recognizes thousands of academic sites and can
          route through your institution's proxy when you are off campus. Everything syncs to
          the free Zotero desktop app, where bibliographies generate in thousands of styles.
          Start building the library in your first semester, not the week before a thesis is due.
        </P>
        <P>
          <MuiLink href="https://www.mybib.com" target="_blank" rel="noopener noreferrer">MyBib</MuiLink>{' '}
          is free with no ads and takes about ten seconds to learn: click, get a formatted
          citation in APA, MLA, Harvard, Chicago, or thousands of other styles, export to Zotero
          or BibTeX later if you outgrow it. It generates citations but does not manage a
          library of PDFs and annotations. For a few papers a semester that is the right amount
          of tool. The{' '}
          <MuiLink href="https://chromewebstore.google.com/detail/google-scholar-button/jboeelhbhgelnapiaogklidcaldheloc" target="_blank" rel="noopener noreferrer">Google Scholar Button</MuiLink>{' '}
          rounds this out — highlight a paper title anywhere, click, and get results plus a
          copyable citation without opening a new tab.
        </P>
      </Section>

      <Section id="writing" title="Writing checks">
        <P>
          <MuiLink href="https://www.grammarly.com" target="_blank" rel="noopener noreferrer">Grammarly</MuiLink>'s{' '}
          free tier catches grammar, spelling, and punctuation as you type across Gmail, Google
          Docs, Canvas, and effectively every text field you use, and it is not a crippled
          trial. Premium (around $12/month) adds rewrites, tone control, and plagiarism
          detection. Grammar checking is treated like a spell checker in academic settings;
          Premium's generative writing feature is the part to leave alone on submitted work.
        </P>
        <P>
          <MuiLink href="https://languagetool.org" target="_blank" rel="noopener noreferrer">LanguageTool</MuiLink>{' '}
          covers 25+ languages and is the better fit for multilingual and ESL writers, with
          context-aware suggestions and a personal dictionary for field terminology. Note that
          since January 2025 the browser extension requires a paid subscription; the web version
          remains free.
        </P>
      </Section>

      <Section id="ai" title="AI assistants, used carefully">
        <P>
          The official{' '}
          <MuiLink href="https://chromewebstore.google.com/detail/chatgpt" target="_blank" rel="noopener noreferrer">ChatGPT extension</MuiLink>{' '}
          (free tier, Plus around $20/month) puts an assistant in a sidebar next to whatever you
          are reading — good for having a concept explained three different ways until it
          clicks.{' '}
          <MuiLink href="https://www.perplexity.ai" target="_blank" rel="noopener noreferrer">Perplexity</MuiLink>{' '}
          (free tier, Pro around $20/month) answers with linked sources every time, which makes
          it the more useful of the two in the early stages of research when you need pointers
          to primary material.
        </P>
        <P>
          The line that matters: nearly every university now has an AI policy, and the common
          shape of it is that using AI to understand material is fine while submitting
          AI-generated text as your own is not. Ask your instructor rather than assuming, and
          disclose when in doubt.
        </P>
      </Section>

      <Section id="reading" title="Reading comfort">
        <P>
          <MuiLink href="https://darkreader.org" target="_blank" rel="noopener noreferrer">Dark Reader</MuiLink>{' '}
          (free, open source) generates a dark theme for sites that have none — including most
          university portals, Canvas, Blackboard, and academic databases still styled like it is
          2005. At 11 PM it is the difference between reading and squinting.{' '}
          <MuiLink href="https://chromewebstore.google.com/detail/mercury-reader" target="_blank" rel="noopener noreferrer">Mercury Reader</MuiLink>{' '}
          (free) strips ads, sidebars, and clutter from an assigned reading and leaves the text,
          with a keyboard shortcut and a send-to-Kindle option.
        </P>
      </Section>

      <Section id="comparison-table" title="At a glance">
        <TableContainer component={Paper} variant="outlined" sx={{ mb: 3 }}>
          <Table size="small" sx={{ minWidth: 720 }} aria-label="Comparison of Chrome extensions for students">
            <TableHead sx={{ bgcolor: 'background.default' }}>
              <TableRow>
                <TableCell><strong>Extension</strong></TableCell>
                <TableCell><strong>Category</strong></TableCell>
                <TableCell><strong>Price</strong></TableCell>
                <TableCell align="center"><strong>Cross-device sync</strong></TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              <TableRow sx={{ bgcolor: 'primary.light', '& td': { fontWeight: 500 } }}><TableCell>Blackmount</TableCell><TableCell>Sessions and tabs</TableCell><TableCell>Free / $0.99 mo</TableCell><TableCell align="center"><strong>Yes</strong></TableCell></TableRow>
              <TableRow><TableCell>Tab Session Manager</TableCell><TableCell>Sessions and tabs</TableCell><TableCell>Free</TableCell><TableCell align="center">—</TableCell></TableRow>
              <TableRow><TableCell>Zotero Connector</TableCell><TableCell>Citations</TableCell><TableCell>Free</TableCell><TableCell align="center">Via Zotero</TableCell></TableRow>
              <TableRow><TableCell>MyBib</TableCell><TableCell>Citations</TableCell><TableCell>Free</TableCell><TableCell align="center">—</TableCell></TableRow>
              <TableRow><TableCell>Grammarly</TableCell><TableCell>Writing</TableCell><TableCell>Free / ~$12 mo</TableCell><TableCell align="center">Account-based</TableCell></TableRow>
              <TableRow><TableCell>Forest</TableCell><TableCell>Focus</TableCell><TableCell>Free</TableCell><TableCell align="center">—</TableCell></TableRow>
              <TableRow><TableCell>StayFocusd</TableCell><TableCell>Focus</TableCell><TableCell>Free</TableCell><TableCell align="center">Partial</TableCell></TableRow>
              <TableRow><TableCell>Dark Reader</TableCell><TableCell>Reading</TableCell><TableCell>Free</TableCell><TableCell align="center">—</TableCell></TableRow>
              <TableRow><TableCell>Perplexity</TableCell><TableCell>AI assistant</TableCell><TableCell>Free / ~$20 mo</TableCell><TableCell align="center">Account-based</TableCell></TableRow>
            </TableBody>
          </Table>
        </TableContainer>
      </Section>

      <Section id="starter" title="If you only install five">
        <Box component="ul" sx={{ pl: 3, mb: 2, '& li': { mb: 1, lineHeight: 1.7 } }}>
          <li><strong>Blackmount</strong> — save and restore the research sessions you keep rebuilding from scratch. Guest Mode is free and needs no account.</li>
          <li><strong>Zotero Connector</strong> — start the citation library on day one; every source saved now is one you do not hunt down later.</li>
          <li><strong>Grammarly</strong> — a baseline quality check on everything you type, everywhere.</li>
          <li><strong>Forest</strong> or <strong>StayFocusd</strong> — pick encouragement or enforcement, whichever your self-control actually needs.</li>
          <li><strong>Dark Reader</strong> — the cheapest quality-of-life upgrade on this page.</li>
        </Box>
        <P>
          Spending nothing at all still gets you most of the way: Tab Session Manager, Google
          Keep, Forest, Grammarly's free tier, Dark Reader, the Google Scholar Button, and
          MyBib. You give up cloud sync and cross-device restore, which is the gap you will feel
          first when you start working from more than one machine.
        </P>
      </Section>

      <Divider sx={{ my: 5 }} />

      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        Extension pricing and browser support change constantly. Spotted an inaccuracy, or a
        tool worth adding? Email{' '}
        <MuiLink href="mailto:support@blackmount.ai">support@blackmount.ai</MuiLink> — we update
        this post as tools ship new versions.
      </Typography>
    </>
  );
}

export default { ...meta, render: Body };
